import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SimpleBar from "simplebar-react";
import { Spinner, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import Avatar from "components/common/Avatar";
import AttendanceMarker from "./AttendanceMarker";
import AttendanceLegend from "./AttendanceLegend";
import AttendanceViewModal from "./AttendanceViewModal";
import AttendanceForm from "./AttendanceForm";
import {
  getAttendance,
  getAttendanceSheet,
  type AttendanceSheetListData,
} from "./services/EmployeeAttendanceService";
import type {
  AttendanceDetails,
  AttendanceSheet,
  AttendanceSheetHoliday,
  DayCell,
} from "./types/employee-attendance.types";
import type { Employee } from "features/Employees/types/employee.types";
import { formatDate } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

/* ---- helpers ---- */

function resolveCell(
  raw: DayCell | null | undefined,
  formattedDate: string,
  today: string,
): DayCell {
  if (raw?.type) return raw as DayCell;
  return formattedDate > today ? { type: "FUTURE" } : { type: "PENDING" };
}

function currentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/* ---- sticky styles (CSS vars adapt to dark/light mode) ---- */

const S = {
  stickyTh: {
    position: "sticky" as const,
    left: 0,
    zIndex: 5,
    background: "var(--bs-body-bg)",
    boxShadow: "2px 0 5px rgba(0,0,0,0.08)",
  },
  stickyTd: {
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    background: "var(--bs-body-bg)",
    boxShadow: "2px 0 5px rgba(0,0,0,0.08)",
  },
  headerTh: {
    position: "sticky" as const,
    top: 0,
    zIndex: 3,
    background: "var(--bs-body-bg)",
    borderBottom: "1px solid var(--bs-border-color)",
  },
  weekend: {
    background: "var(--bs-tertiary-bg, rgba(0,0,0,0.025))",
  } as React.CSSProperties,
  holiday: { background: "rgba(255,193,7,0.07)" } as React.CSSProperties,
};

/* ================================================================
   AttendanceGrid
   ================================================================ */

const AttendanceGrid = ({
  lockedEmployeeId,
}: {
  lockedEmployeeId?: number | null;
}) => {
  const { can } = useAccess();
  const canMarkAttendance =
    can(Permissions.EMPLOYEE_ATTENDANCE.CREATE) ||
    can(Permissions.EMPLOYEE_ATTENDANCE.UPDATE);
  const canViewAttendance =
    canMarkAttendance || can(Permissions.EMPLOYEE_ATTENDANCE.READ);

  /* ---- state ---- */
  const [month, setMonth] = useState(currentYearMonth);
  const [rows, setRows] = useState<AttendanceSheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [daysInMonth, setDaysInMonth] = useState<number>(() => {
    const [y, m] = currentYearMonth().split("-");
    return new Date(Number(y), Number(m), 0).getDate();
  });
  const [holidays, setHolidays] = useState<AttendanceSheetHoliday[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 20;

  /* ---- modals ---- */
  const [showViewModal, setShowViewModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [attendance, setAttendance] = useState<AttendanceDetails | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [selectedDate, setSelectedDate] = useState("");

  /* ---- derived ---- */
  const [year, monthNum] = useMemo(() => {
    const p = month.split("-");
    return [Number(p[0]), Number(p[1])];
  }, [month]);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const holidayByDay = useMemo(() => {
    const map: Record<number, AttendanceSheetHoliday> = {};
    holidays.forEach((h) => {
      map[h.day] = h;
    });
    return map;
  }, [holidays]);

  const todayDay = useMemo(() => {
    const todayMonth = today.slice(0, 7);
    return month === todayMonth ? Number(today.slice(8, 10)) : null;
  }, [month, today]);

  const days = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, i) => {
        const day = i + 1;
        const date = new Date(year, monthNum - 1, day);
        const dow = date.getDay();
        return {
          day,
          weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
          isWeekend: dow === 0 || dow === 6,
          isToday: day === todayDay,
          formattedDate: formatDate(year, monthNum - 1, day),
          holiday: holidayByDay[day] ?? null,
        };
      }),
    [daysInMonth, year, monthNum, holidayByDay, todayDay],
  );

  /* ---- drag-to-scroll ---- */
  const dragWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = dragWrapperRef.current;
    if (!wrapper) return;

    const getScrollEl = (): HTMLElement =>
      (wrapper.querySelector(".simplebar-content-wrapper") as HTMLElement | null) ?? wrapper;

    const drag = { active: false, startX: 0, scrollStart: 0, el: null as HTMLElement | null };

    const onDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) return;
      const el = getScrollEl();
      drag.active = true;
      drag.startX = e.pageX;
      drag.scrollStart = el.scrollLeft;
      drag.el = el;
      wrapper.style.cursor = "grabbing";
      wrapper.style.userSelect = "none";
    };

    const onMove = (e: MouseEvent) => {
      if (!drag.active || !drag.el) return;
      drag.el.scrollLeft = drag.scrollStart - (e.pageX - drag.startX);
    };

    const onUp = () => {
      if (!drag.active) return;
      drag.active = false;
      drag.el = null;
      wrapper.style.cursor = "grab";
      wrapper.style.userSelect = "";
    };

    wrapper.style.cursor = "grab";
    wrapper.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      wrapper.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  /* ---- scroll today into view when viewing current month ---- */
  useEffect(() => {
    const todayDate = new Date();
    const todayMonth = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, "0")}`;
    if (month !== todayMonth) return;
    const todayDay = todayDate.getDate();

    // Wait one frame so the DOM has painted the new columns
    const raf = requestAnimationFrame(() => {
      const wrapper = dragWrapperRef.current;
      if (!wrapper) return;
      const scrollEl = wrapper.querySelector(".simplebar-content-wrapper") as HTMLElement | null;
      const todayTh = wrapper.querySelector(`th[data-day="${todayDay}"]`) as HTMLElement | null;
      if (!scrollEl || !todayTh) return;
      const target = todayTh.offsetLeft - scrollEl.clientWidth / 2 + todayTh.offsetWidth / 2;
      scrollEl.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [month]);

  /* ---- search debounce ---- */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  /* ---- reset page on filter change ---- */
  useEffect(() => {
    setPage(1);
  }, [month, debouncedSearch]);

  /* ---- fetch ---- */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAttendanceSheet({
        month: monthNum,
        year,
        page,
        limit,
        search: debouncedSearch || undefined,
        ...(lockedEmployeeId != null ? { employeeId: lockedEmployeeId } : {}),
      });
      if (res.success && res.data) {
        const d = res.data as AttendanceSheetListData;
        setRows((d as any).rows ?? []);
        setTotalPages((d as any).totalPages ?? 1);
        setTotalItems((d as any).totalItems ?? 0);
        if (d.daysInMonth) setDaysInMonth(d.daysInMonth);
        if (d.holidays) setHolidays(d.holidays);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [monthNum, year, page, limit, debouncedSearch, lockedEmployeeId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  /* ---- month navigation ---- */
  const navigateMonth = (delta: number) =>
    setMonth(moment(month, "YYYY-MM").add(delta, "months").format("YYYY-MM"));

  /* ---- cell click ---- */
  const fetchAttendanceDetails = async (employeeId: number, date: string) => {
    try {
      const res = await getAttendance({ employeeId, date });
      if (res.success) setAttendance(res.data ?? null);
    } catch {
      setAttendance(null);
    }
  };

  const handleCellClick = (
    row: AttendanceSheet,
    formattedDate: string,
    isPending: boolean,
  ) => {
    setAttendance(null);
    setEmployee(row.employee);
    setSelectedDate(formattedDate);
    if (isPending && canMarkAttendance) {
      setShowForm(true);
    } else if (!isPending) {
      setShowViewModal(true);
      void fetchAttendanceDetails(row.employee.id, formattedDate);
    }
  };

  /* ================================================================
     Render
     ================================================================ */
  return (
    <>
      <AttendanceViewModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        onEdit={() => {
          setShowViewModal(false);
          setShowForm(true);
        }}
        selectedDate={selectedDate}
        data={attendance}
        canEdit={canMarkAttendance}
      />
      <AttendanceForm
        show={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={() => {
          setShowForm(false);
          void loadData();
        }}
        selectedDate={selectedDate}
        employeeId={employee?.id ?? 0}
        joinDate={employee?.joinDate ?? ""}
        data={attendance}
      />

      <AttendanceLegend />

      {/* ---- Toolbar ---- */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        {/* Search */}
        <div
          className="position-relative"
          style={{ maxWidth: 260, flex: "1 1 180px" }}
        >
          <i
            className="ri-search-line position-absolute text-muted"
            style={{
              left: 9,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "0.85rem",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            className="form-control form-control-sm ps-4"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Month navigation */}
        <div className="d-flex align-items-center gap-1 ms-auto">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigateMonth(-1)}
            aria-label="Previous month"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <span
            className="fw-medium small px-1"
            style={{ minWidth: 110, textAlign: "center" }}
          >
            {moment(month, "YYYY-MM").format("MMMM YYYY")}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigateMonth(1)}
            aria-label="Next month"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      {/* ---- Table (SimpleBar handles horizontal scroll) ---- */}
      <div ref={dragWrapperRef}>
      <SimpleBar style={{ overflowX: "auto" }}>
        <table
          className="table table-nowrap align-middle mb-0"
          style={{
            minWidth: 600,
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead>
            <tr>
              {/* Employee column header — sticky left + sticky top */}
              <th
                style={{
                  ...S.stickyTh,
                  ...S.headerTh,
                  zIndex: 6,
                  minWidth: 230,
                  width: 230,
                  padding: "10px 12px",
                }}
                className="bg-light-subtle"
              >
                Employee
              </th>

              {days.map(({ day, weekday, isWeekend, isToday, holiday }) => {
                const hid = `ag-h-${day}`;
                const colBg = isToday
                  ? { background: "rgba(13,110,253,0.07)" }
                  : holiday
                    ? S.holiday
                    : isWeekend
                      ? S.weekend
                      : {};
                return (
                  <th
                    key={day}
                    data-day={day}
                    style={{
                      ...S.headerTh,
                      ...colBg,
                      textAlign: "center",
                      minWidth: 46,
                      width: 46,
                      padding: "6px 2px",
                    }}
                  >
                    {isToday ? (
                      <div
                        className="fw-bold lh-1 rounded-circle text-white bg-primary d-inline-flex align-items-center justify-content-center mx-auto"
                        style={{ width: 24, height: 24, fontSize: "0.72rem" }}
                      >
                        {day}
                      </div>
                    ) : holiday ? (
                      <>
                        <div id={hid} className="fw-bold text-warning lh-1">
                          {day}
                        </div>
                        <UncontrolledTooltip target={hid} placement="top">
                          {holiday.name}
                          {holiday.kind ? ` · ${holiday.kind}` : ""}
                          {holiday.isPaid ? " · Paid" : ""}
                        </UncontrolledTooltip>
                      </>
                    ) : (
                      <div className={`fw-bold lh-1${isWeekend ? " text-muted" : ""}`}>
                        {day}
                      </div>
                    )}
                    <div className="text-muted lh-1 mt-1" style={{ fontSize: "0.6rem" }}>
                      {weekday}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={days.length + 1}
                  className="text-center py-5 border-0"
                >
                  <Spinner size="sm" color="primary" className="me-2" />
                  <span className="text-muted small">Loading…</span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={days.length + 1}
                  className="text-center text-muted py-4 border-0"
                >
                  No records found
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const emp = row.employee;
                const name = emp?.user
                  ? `${emp.user.firstName ?? ""} ${emp.user.lastName ?? ""}`.trim()
                  : "—";

                return (
                  <tr key={emp.id}>
                    {/* Sticky employee cell */}
                    <td
                      style={{ ...S.stickyTd, padding: "8px 12px" }}
                      className="bg-light-subtle"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div className="avatar-sm flex-shrink-0">
                          <Avatar
                            name={
                              name === "—"
                                ? (emp.user?.email ?? "Employee")
                                : name
                            }
                            image={emp.user?.avatar}
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="fw-medium text-truncate"
                            style={{ maxWidth: 150 }}
                          >
                            <Link
                              to={`/employees/${emp.id}`}
                              className="text-body"
                            >
                              {name}
                            </Link>
                          </div>
                          <div className="text-muted small text-truncate" style={{ maxWidth: 150 }}>
                            {emp.user?.email ?? "—"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Day cells */}
                    {days.map(({ day, isWeekend, isToday, formattedDate, holiday }) => {
                      const raw = row.days?.[day];
                      let cell = resolveCell(raw, formattedDate, today);

                      if (cell.type === "BEFORE_JOINING" && holiday) {
                        cell = {
                          type: "HOLIDAY",
                          holidayName: holiday.name,
                          holidayKind: holiday.kind ?? undefined,
                          isPaidHoliday: holiday.isPaid,
                        };
                      }

                      const isPending = cell.type === "PENDING";
                      const isNotNavigable =
                        cell.type === "FUTURE" ||
                        cell.type === "BEFORE_JOINING";
                      const isClickable = isNotNavigable
                        ? false
                        : isPending
                          ? !cell.isLocked && canMarkAttendance
                          : canViewAttendance;

                      const colBg = isToday
                        ? { background: "rgba(13,110,253,0.07)" }
                        : holiday
                          ? S.holiday
                          : isWeekend
                            ? S.weekend
                            : {};

                      return (
                        <td
                          key={day}
                          style={{
                            ...colBg,
                            textAlign: "center",
                            padding: "4px 2px",
                          }}
                        >
                          <AttendanceMarker
                            cell={cell}
                            day={day}
                            employeeId={emp.id}
                            disabled={!isClickable}
                            onClick={() =>
                              handleCellClick(row, formattedDate, isPending)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </SimpleBar>
      </div>

      {/* ---- Pagination ---- */}
      {(totalPages > 1 || totalItems > 0) && (
        <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
          <span className="text-muted small">
            {totalItems} employee{totalItems !== 1 ? "s" : ""}
          </span>
          {totalPages > 1 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="small px-2">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AttendanceGrid;
